import React, { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Menu, X, MessageCircle, UserCircle } from "lucide-react";
import { LOGO_URL, NAV_LINKS, WHATSAPP_NUMBER } from "@/lib/constants";
import { useAuth } from "@/context/AuthContext";

export const Header = () => {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuth();
  const memberHref =
    user && (user.role === "member" || user.role === "admin")
      ? "/espace-membre"
      : "/espace-membre/connexion";

  return (
    <header
      data-testid="site-header"
      className="sticky top-0 z-50 bg-white/95 backdrop-blur-md border-b border-border"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          <Link to="/" data-testid="header-logo-link" className="flex items-center gap-3 shrink-0">
            <img src={LOGO_URL} alt="DEFAR - Les Bâtisseurs" className="h-12 w-auto" />
          </Link>

          <nav className="hidden lg:flex items-center gap-7">
            {NAV_LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`nav-${l.to === "/" ? "home" : l.to.replace("/", "")}`}
                className={({ isActive }) =>
                  `text-sm font-medium transition-colors ${
                    isActive ? "text-brand-blue" : "text-slate-700 hover:text-brand-blue"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-3">
            <button
              data-testid="header-member-link"
              onClick={() => navigate("/espace-membre/connexion")}
              className="flex items-center gap-1.5 text-sm font-medium text-slate-700 hover:text-brand-blue transition-colors"
            >
              <UserCircle size={18} /> DEFAR & Moi
            </button>
            <a
              href={`https://wa.me/${WHATSAPP_NUMBER}`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="header-whatsapp"
              className="flex items-center justify-center h-10 w-10 border-2 border-brand-green text-brand-green hover:bg-brand-green hover:text-white transition-colors"
              aria-label="WhatsApp"
            >
              <MessageCircle size={18} />
            </a>
            <button
              data-testid="header-soutenir-btn"
              onClick={() => navigate("/adhesion?type=soutien")}
              className="border-2 border-brand-blue text-brand-blue hover:bg-brand-blue hover:text-white transition-colors px-5 py-2 text-sm font-semibold"
            >
              Soutenir
            </button>
            <button
              data-testid="header-rejoindre-btn"
              onClick={() => navigate("/adhesion")}
              className="bg-brand-green text-white hover:bg-brand-greenhover transition-colors px-5 py-2 text-sm font-semibold"
            >
              Rejoindre DEFAR
            </button>
          </div>

          <button
            data-testid="mobile-menu-toggle"
            className="lg:hidden p-2 text-brand-blue"
            onClick={() => setOpen(!open)}
            aria-label="Menu"
          >
            {open ? <X size={26} /> : <Menu size={26} />}
          </button>
        </div>
      </div>

      {open && (
        <div data-testid="mobile-menu" className="lg:hidden border-t border-border bg-white">
          <nav className="px-4 py-4 flex flex-col gap-1">
            {NAV_LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  `py-3 px-2 text-base font-medium border-b border-slate-100 ${
                    isActive ? "text-brand-blue" : "text-slate-700"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <div className="flex gap-3 mt-4">
              <button
                onClick={() => { setOpen(false); navigate("/adhesion?type=soutien"); }}
                className="flex-1 border-2 border-brand-blue text-brand-blue px-4 py-3 text-sm font-semibold"
              >
                Soutenir
              </button>
              <button
                onClick={() => { setOpen(false); navigate("/adhesion"); }}
                className="flex-1 bg-brand-green text-white px-4 py-3 text-sm font-semibold"
              >
                Rejoindre
              </button>
            </div>
            <button
              onClick={() => { setOpen(false); navigate(memberHref); }}
              className="mt-3 w-full flex items-center justify-center gap-2 border border-border text-slate-700 px-4 py-3 text-sm font-semibold"
            >
              <UserCircle size={18} /> DEFAR & Moi
            </button>
          </nav>
        </div>
      )}
    </header>
  );
};
